import { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Checkbox,
  Icon,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Divider,
  Heading,
  UnorderedList,
  ListItem,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
} from '@chakra-ui/react';
import { Sparkles, AlertTriangle, FileText, X } from 'lucide-react';

interface AITermsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
  onDecline: () => void;
}

// Terms version is managed by the backend Firebase Cloud Function
// const CURRENT_TERMS_VERSION = 'v1.0';

/**
 * AI Terms Modal Component
 * Modal for accepting AI terms before using AI evaluation features
 * Translated from Tailwind to Chakra UI
 */
export function AITermsModal({ isOpen, onClose, onAccept, onDecline }: AITermsModalProps) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setAcknowledged(false);
      setShowPolicy(false);
      setHasScrolledToBottom(false);
      setIsLoading(false);
    }
  }, [isOpen]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const isAtBottom = Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) < 10;
    if (isAtBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  const handleAccept = async () => {
    setIsLoading(true);
    try {
      // The actual acceptance is handled by the parent component via onAccept
      await onAccept();
      // If onAccept succeeds, the parent will close the modal
    } catch (error) {
      console.error('Error accepting terms:', error);
      toast({
        title: 'Error',
        description: 'Failed to record terms acceptance. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (showPolicy) {
    return (
      <Modal isOpen={isOpen} onClose={onDecline} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader>
            <HStack justify="space-between">
              <Heading size="md">AI User Acknowledgement Policy</Heading>
              <Button variant="ghost" size="sm" onClick={() => setShowPolicy(false)}>
                <Icon as={X} w={5} h={5} />
              </Button>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box
              maxH="500px"
              overflowY="auto"
              pr={2}
              onScroll={handleScroll}
            >
              <VStack spacing={6} align="stretch">
                <Box textAlign="center">
                  <Heading size="lg" mb={2}>AI-Powered Rock Evaluation</Heading>
                  <Heading size="md" mb={2}>End User Acknowledgement Policy</Heading>
                  <Text fontStyle="italic" color="gray.600" mb={2}>
                    Future Home Loans Leadership Team
                  </Text>
                  <Box bg="blue.50" border="1px" borderColor="blue.200" borderRadius="md" p={4} display="inline-block">
                    <Text fontSize="sm" fontWeight="medium" color="blue.900">
                      Policy Version 1.0 | Effective Date: November 4, 2025
                    </Text>
                  </Box>
                </Box>

                <Box>
                  <Heading size="md" color="blue.900" mb={3}>Purpose and Scope</Heading>
                  <Text mb={3}>
                    This policy governs the use of artificial intelligence-powered Rock evaluation features within Future Home Loans' Entrepreneurial Operating System (EOS) platform. The AI feature provides leadership team members with automated quality assessments and improvement recommendations for their quarterly Rocks (90-day priorities).
                  </Text>
                  <Text>
                    <Text as="span" fontWeight="bold">Scope:</Text> This policy applies to all Future Home Loans leadership team members who choose to use AI-powered Rock evaluation or related AI features. Use of these features is voluntary and requires explicit acknowledgement of this policy.
                  </Text>
                </Box>

                <Box>
                  <Heading size="md" color="blue.900" mb={3}>What the AI Feature Does</Heading>
                  <Text mb={3}>
                    The AI-powered Rock evaluation feature analyzes your quarterly Rocks and provides feedback on:
                  </Text>
                  <UnorderedList spacing={2} mb={3} pl={6}>
                    <ListItem>
                      <Text as="span" fontWeight="bold">SMART criteria compliance:</Text> Whether your Rock is Specific, Measurable, Achievable, Relevant, and Time-bound
                    </ListItem>
                    <ListItem>
                      <Text as="span" fontWeight="bold">Clarity and measurability:</Text> How clearly your success criteria are defined
                    </ListItem>
                    <ListItem>
                      <Text as="span" fontWeight="bold">Milestone alignment:</Text> How well your milestones support achieving the overall Rock
                    </ListItem>
                    <ListItem>
                      <Text as="span" fontWeight="bold">Improvement recommendations:</Text> Specific suggestions to strengthen your Rock
                    </ListItem>
                  </UnorderedList>
                  <Text>
                    <Text as="span" fontWeight="bold">Technology:</Text> The feature uses OpenAI's GPT-4 artificial intelligence model to perform analysis and generate recommendations.
                  </Text>
                </Box>

                <Box>
                  <Heading size="md" color="blue.900" mb={3}>Data Transmission and Handling</Heading>
                  <Heading size="sm" mb={2}>What Data Is Sent</Heading>
                  <Text mb={3}>
                    When you request AI evaluation of a Rock, the following information is transmitted to OpenAI:
                  </Text>
                  <UnorderedList spacing={1} mb={4} pl={6}>
                    <ListItem>Rock description and title</ListItem>
                    <ListItem>Rock milestones and completion criteria</ListItem>
                    <ListItem>Progress updates (if evaluating progress)</ListItem>
                    <ListItem>Any other text you include in your Rock or evaluation request</ListItem>
                  </UnorderedList>
                  <Heading size="sm" mb={2}>OpenAI Data Handling</Heading>
                  <Text mb={2}>
                    OpenAI processes your Rock data according to the following terms:
                  </Text>
                  <UnorderedList spacing={1} pl={6}>
                    <ListItem>
                      <Text as="span" fontWeight="bold">Training opt-out:</Text> Future Home Loans has opted out of data sharing. Your Rock data is NOT used to train or improve OpenAI's models.
                    </ListItem>
                    <ListItem>
                      <Text as="span" fontWeight="bold">Retention period:</Text> OpenAI may retain your data for up to 30 days for abuse and misuse monitoring purposes only.
                    </ListItem>
                    <ListItem>
                      <Text as="span" fontWeight="bold">No third-party sharing:</Text> OpenAI does not share your data with third parties.
                    </ListItem>
                  </UnorderedList>
                </Box>

                <Box>
                  <Heading size="md" color="blue.900" mb={3}>User Responsibilities</Heading>
                  <Alert status="warning" mb={4}>
                    <AlertIcon />
                    <AlertTitle>IMPORTANT: Keep Rocks High-Level and Strategic</AlertTitle>
                  </Alert>
                  <Heading size="sm" mb={2}>What You MUST NOT Include in Rocks</Heading>
                  <Text mb={3}>
                    As a member of the leadership team working on strategic EOS goals, you must never include the following types of information in Rocks submitted for AI evaluation:
                  </Text>
                  <Table variant="simple" size="sm" mb={4}>
                    <Thead>
                      <Tr bg="red.600">
                        <Th color="white" border="1px" borderColor="gray.300">Prohibited Information</Th>
                        <Th color="white" border="1px" borderColor="gray.300">Why This Matters</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      <Tr>
                        <Td border="1px" borderColor="gray.300">Client names and identifying information</Td>
                        <Td border="1px" borderColor="gray.300">Protects client privacy and confidentiality obligations</Td>
                      </Tr>
                      <Tr>
                        <Td border="1px" borderColor="gray.300">Employee personal information</Td>
                        <Td border="1px" borderColor="gray.300">Protects employee privacy and complies with HR policies</Td>
                      </Tr>
                      <Tr>
                        <Td border="1px" borderColor="gray.300">Personally identifiable information (PII)</Td>
                        <Td border="1px" borderColor="gray.300">Legal compliance and privacy protection</Td>
                      </Tr>
                    </Tbody>
                  </Table>
                  <Heading size="sm" mb={2}>How to Write Appropriate Rocks</Heading>
                  <Box mb={3}>
                    <Text fontWeight="semibold" color="green.700" mb={2}>Good examples (high-level, strategic):</Text>
                    <UnorderedList spacing={1} pl={4}>
                      <ListItem color="green.700">✓ "Launch new correspondent lending channel with 3 partner banks by Q2 end"</ListItem>
                      <ListItem color="green.700">✓ "Increase operational efficiency by 15% through automation initiatives"</ListItem>
                      <ListItem color="green.700">✓ "Implement new lead tracking system across all loan officers"</ListItem>
                      <ListItem color="green.700">✓ "Build recruiting pipeline with 20 qualified candidates by June 30"</ListItem>
                    </UnorderedList>
                  </Box>
                  <Box mb={4}>
                    <Text fontWeight="semibold" color="red.700" mb={2}>Bad examples (too specific, includes sensitive info):</Text>
                    <UnorderedList spacing={1} pl={4}>
                      <ListItem color="red.700">✗ "Close the Smith family refinance loan for $450,000"</ListItem>
                      <ListItem color="red.700">✗ "Partner with ABC Bank under terms outlined in confidential agreement"</ListItem>
                      <ListItem color="red.700">✗ "Hire John Doe as VP of Operations at $150k salary"</ListItem>
                      <ListItem color="red.700">✗ "Implement cost savings strategy by reducing staffing in Phoenix branch"</ListItem>
                    </UnorderedList>
                  </Box>
                  <Heading size="sm" mb={2}>Additional User Obligations</Heading>
                  <Text mb={2}>By using the AI-powered Rock evaluation feature, you agree to:</Text>
                  <UnorderedList spacing={2} pl={6}>
                    <ListItem>
                      <Text as="span" fontWeight="bold">Comply with company policies:</Text> Follow all Future Home Loans confidentiality, data protection, and acceptable use policies.
                    </ListItem>
                    <ListItem>
                      <Text as="span" fontWeight="bold">Accept full responsibility:</Text> You are solely responsible for the content you submit and any consequences of including inappropriate information.
                    </ListItem>
                    <ListItem>
                      <Text as="span" fontWeight="bold">Use appropriate judgment:</Text> Exercise reasonable care in determining what information is appropriate to include in Rocks submitted for AI evaluation.
                    </ListItem>
                    <ListItem>
                      <Text as="span" fontWeight="bold">Follow acceptable use standards:</Text> Do not attempt to manipulate, abuse, or circumvent the AI system's intended purpose.
                    </ListItem>
                  </UnorderedList>
                </Box>

                <Box>
                  <Heading size="md" color="blue.900" mb={3}>AI Limitations and Disclaimers</Heading>
                  <Heading size="sm" mb={2}>AI Is Advisory Only</Heading>
                  <Text mb={2}>
                    The AI-powered Rock evaluation feature provides suggestions and recommendations only. You must understand and acknowledge:
                  </Text>
                  <UnorderedList spacing={1} mb={4} pl={6}>
                    <ListItem>
                      <Text as="span" fontWeight="bold">Not professional advice:</Text> AI recommendations are not professional consulting, legal, or business advice.
                    </ListItem>
                    <ListItem>
                      <Text as="span" fontWeight="bold">You retain full responsibility:</Text> You are solely responsible for decisions about your Rocks and business strategies.
                    </ListItem>
                    <ListItem>
                      <Text as="span" fontWeight="bold">AI may produce errors:</Text> Artificial intelligence can generate inaccurate, incomplete, or inappropriate recommendations.
                    </ListItem>
                    <ListItem>
                      <Text as="span" fontWeight="bold">Human judgment required:</Text> Always apply your own professional judgment and expertise when evaluating AI suggestions.
                    </ListItem>
                  </UnorderedList>
                  <Heading size="sm" mb={2}>Company Liability</Heading>
                  <Text mb={2}>Future Home Loans provides the AI evaluation feature on an "as-is" basis and:</Text>
                  <UnorderedList spacing={1} pl={6}>
                    <ListItem>Makes no warranties regarding accuracy, completeness, or reliability of AI-generated content</ListItem>
                    <ListItem>Is not liable for decisions made based on AI recommendations</ListItem>
                    <ListItem>Is not responsible for any consequences of including inappropriate information in AI requests</ListItem>
                    <ListItem>Reserves the right to modify or discontinue the AI feature at any time</ListItem>
                  </UnorderedList>
                </Box>

                <Box>
                  <Heading size="md" color="blue.900" mb={3}>Policy Violations</Heading>
                  <Text mb={2}>Violations of this policy may result in:</Text>
                  <UnorderedList spacing={2} mb={3} pl={6}>
                    <ListItem>
                      <Text as="span" fontWeight="bold">Warning notification:</Text> First-time or minor violations may result in a warning and educational guidance.
                    </ListItem>
                    <ListItem>
                      <Text as="span" fontWeight="bold">Temporary suspension:</Text> Repeated violations or inclusion of sensitive information may result in temporary loss of AI feature access.
                    </ListItem>
                    <ListItem>
                      <Text as="span" fontWeight="bold">Permanent revocation:</Text> Serious violations, intentional policy circumvention, or patterns of misuse may result in permanent loss of AI access.
                    </ListItem>
                    <ListItem>
                      <Text as="span" fontWeight="bold">Administrative review:</Text> Unusual patterns or security concerns will trigger management review and potential investigation.
                    </ListItem>
                  </UnorderedList>
                  <Text fontSize="sm" fontStyle="italic" color="gray.600">
                    Note: Policy violations may also result in disciplinary action under general company employment policies if they involve confidentiality breaches or other misconduct.
                  </Text>
                </Box>

                <Box>
                  <Heading size="md" color="blue.900" mb={3}>Acceptance and Acknowledgement</Heading>
                  <Heading size="sm" mb={2}>Required Acknowledgement</Heading>
                  <Text mb={3}>
                    Before using AI-powered Rock evaluation features, you must explicitly acknowledge:
                  </Text>
                  <Box bg="gray.100" border="2px" borderColor="gray.300" borderRadius="md" p={4} mb={4} textAlign="center" fontStyle="italic">
                    "I acknowledge that my Rock data will be sent to OpenAI for analysis, and I will adhere to Future Home Loans End User Acknowledgement Policy."
                  </Box>
                  <Text mb={2}>By checking the acknowledgement box and clicking to proceed, you confirm that you have:</Text>
                  <UnorderedList spacing={1} mb={4} pl={6}>
                    <ListItem>Read and understood this entire policy</ListItem>
                    <ListItem>Agree to comply with all user responsibilities outlined herein</ListItem>
                    <ListItem>Understand data will be transmitted to OpenAI and retained for up to 30 days</ListItem>
                    <ListItem>Will keep all Rocks high-level and strategic, excluding client and confidential information</ListItem>
                    <ListItem>Accept that AI recommendations are advisory only and that you retain full decision-making responsibility</ListItem>
                  </UnorderedList>
                  <Heading size="sm" mb={2}>Terms Version</Heading>
                  <Text>
                    This policy is Version 1.0. Your acceptance is recorded and associated with this specific version. If the policy is updated, you may be required to review and acknowledge the new version before continuing to use AI features.
                  </Text>
                </Box>

                <Divider />
                <Box textAlign="center" fontSize="sm" fontStyle="italic" color="gray.600">
                  <Text>By using AI features, you acknowledge acceptance of this policy</Text>
                  <Text>Future Home Loans - AI Usage Policy</Text>
                </Box>
              </VStack>
            </Box>

            {!hasScrolledToBottom && (
              <Alert status="warning" mt={4}>
                <AlertIcon />
                <AlertDescription fontSize="xs">
                  Please scroll to the bottom of the policy to continue.
                </AlertDescription>
              </Alert>
            )}

            {hasScrolledToBottom && (
              <Alert status="success" mt={4}>
                <AlertIcon />
                <AlertDescription fontSize="xs">
                  You have reviewed the complete policy. You may now close this window and accept the terms.
                </AlertDescription>
              </Alert>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              onClick={() => setShowPolicy(false)}
              isDisabled={!hasScrolledToBottom}
            >
              Close Policy
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onDecline} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <VStack spacing={4} align="center">
            <Box
              w={16}
              h={16}
              bgGradient="linear(to-br, blue.500, blue.700)"
              borderRadius="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={Sparkles} w={8} h={8} color="white" />
            </Box>
            <Heading size="lg">Try AI-Powered Rock Evaluation</Heading>
            <Text color="gray.600">Get instant feedback on your Rocks using GPT-4</Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <HStack spacing={3} align="start">
              <Box flexShrink={0} w={6} h={6} bg="green.100" borderRadius="full" display="flex" alignItems="center" justifyContent="center" mt={0.5}>
                <Text color="green.600" fontSize="sm" fontWeight="bold">✓</Text>
              </Box>
              <Box>
                <Text fontSize="sm" fontWeight="medium">Checks if your Rock is SMART</Text>
                <Text fontSize="xs" color="gray.600">Specific, Measurable, Achievable, Relevant, Time-bound</Text>
              </Box>
            </HStack>

            <HStack spacing={3} align="start">
              <Box flexShrink={0} w={6} h={6} bg="green.100" borderRadius="full" display="flex" alignItems="center" justifyContent="center" mt={0.5}>
                <Text color="green.600" fontSize="sm" fontWeight="bold">✓</Text>
              </Box>
              <Box>
                <Text fontSize="sm" fontWeight="medium">Suggests improvements</Text>
                <Text fontSize="xs" color="gray.600">AI provides actionable recommendations</Text>
              </Box>
            </HStack>

            <HStack spacing={3} align="start">
              <Box flexShrink={0} w={6} h={6} bg="green.100" borderRadius="full" display="flex" alignItems="center" justifyContent="center" mt={0.5}>
                <Text color="green.600" fontSize="sm" fontWeight="bold">✓</Text>
              </Box>
              <Box>
                <Text fontSize="sm" fontWeight="medium">Evaluates milestones alignment</Text>
                <Text fontSize="xs" color="gray.600">Ensures your plan supports the goal</Text>
              </Box>
            </HStack>

            <Box bg="blue.50" border="2px" borderColor="blue.300" borderRadius="lg" p={4}>
              <HStack spacing={3} align="start">
                <Icon as={FileText} w={5} h={5} color="blue.600" flexShrink={0} mt={0.5} />
                <Box flex={1}>
                  <Text fontSize="sm" fontWeight="semibold" color="blue.900" mb={2}>
                    Required: Review Policy Document
                  </Text>
                  <Text fontSize="xs" color="blue.800" mb={3}>
                    You must review the AI User Acknowledgement Policy before proceeding.
                  </Text>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    leftIcon={<Icon as={FileText} w={4} h={4} />}
                    onClick={() => setShowPolicy(true)}
                  >
                    View Policy Document
                  </Button>
                  {hasScrolledToBottom && (
                    <HStack spacing={2} mt={2}>
                      <Box w={4} h={4} bg="green.100" borderRadius="full" display="flex" alignItems="center" justifyContent="center">
                        <Text color="green.600" fontSize="xs" fontWeight="bold">✓</Text>
                      </Box>
                      <Text fontSize="xs" color="green.700">Policy reviewed</Text>
                    </HStack>
                  )}
                </Box>
              </HStack>
            </Box>

            <Divider />

            <Checkbox
              isChecked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              isDisabled={!hasScrolledToBottom}
              colorScheme="blue"
            >
              <Text fontSize="sm">
                I have reviewed and agree to the Future Home Loans AI-Powered Rock Evaluation End User Acknowledgement Policy.
              </Text>
            </Checkbox>
            {!hasScrolledToBottom && (
              <Alert status="warning" size="sm">
                <AlertIcon as={AlertTriangle} />
                <AlertDescription fontSize="xs">
                  You must view and scroll through the complete policy document before accepting.
                </AlertDescription>
              </Alert>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onDecline} flex={1}>
            Not Now
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleAccept}
            isDisabled={!acknowledged || !hasScrolledToBottom || isLoading}
            isLoading={isLoading}
            loadingText="Processing..."
            flex={1}
          >
            I Accept
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

